import { useState, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { AppShell } from '../../../components/layout';
import { Button, Card, StepProgressBar } from '../../../components/ui';
import { ArticleDisplay, OfflinePrompt } from '../../../components/stimulus';
import {
  QuestionCard,
  QuestionNavigation,
  MultipleChoice,
  ShortAnswer,
  EssayResponse,
  ImageUpload,
} from '../../../components/questions';

export default function PracticeSession({
  student,
  session,
  stimulus,
  questions,
  current_question_index,
}) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(session.current_step || 'stimulus');
  const [questionIndex, setQuestionIndex] = useState(current_question_index || 0);
  const [responses, setResponses] = useState(() => {
    // Initialize responses from questions
    const initial = {};
    questions.forEach((q) => {
      initial[q.id] = q.current_response || '';
    });
    return initial;
  });
  const [uploadedImages, setUploadedImages] = useState({});

  const currentQuestion = questions[questionIndex];
  const totalQuestions = questions.length;
  const stepLabels = [t('practice.reading'), t('practice.questions'), t('practice.reviewLabel')];
  const currentStepIndex = currentStep === 'stimulus' ? 1 : currentStep === 'questions' ? 2 : 3;

  const handleContinueToQuestions = useCallback(() => {
    setCurrentStep('questions');
  }, []);

  const handleResponseChange = useCallback((questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  }, []);

  const handleImageUpload = useCallback((questionId, imageData) => {
    setUploadedImages((prev) => ({
      ...prev,
      [questionId]: imageData.previewUrl,
    }));
  }, []);

  const handleRemoveImage = useCallback((questionId) => {
    setUploadedImages((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  }, []);

  const handlePreviousQuestion = useCallback(() => {
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    }
  }, [questionIndex]);

  const handleNextQuestion = useCallback(() => {
    if (questionIndex < totalQuestions - 1) {
      setQuestionIndex(questionIndex + 1);
    } else {
      // Submit all responses
      handleSubmit();
    }
  }, [questionIndex, totalQuestions]);

  const handleSubmit = useCallback(() => {
    // In a real app, we would submit responses to the server here
    router.visit(`/student/practice/${session.id}/results`);
  }, [session.id]);

  const handleSaveAndExit = useCallback(() => {
    // Save progress and return to dashboard
    router.visit('/');
  }, []);

  const canProceedToNext = () => {
    if (!currentQuestion) return false;
    const response = responses[currentQuestion.id];
    return response && response.trim().length > 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('practice.step')} {currentStepIndex} {t('common.of')} 3 — {stepLabels[currentStepIndex - 1]}
            </span>
            <Button variant="ghost" size="sm" onClick={handleSaveAndExit}>
              {t('practice.saveAndExit')}
            </Button>
          </div>
          <StepProgressBar
            currentStep={currentStepIndex}
            totalSteps={3}
            stepLabels={stepLabels}
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Stimulus View */}
        {currentStep === 'stimulus' && (
          <div className="space-y-6">
            <ArticleDisplay
              title={stimulus.title}
              subtitle={stimulus.subtitle}
              content={stimulus.content}
              estimatedReadTime={stimulus.estimated_read_time}
            />
            <OfflinePrompt onContinue={handleContinueToQuestions} />
          </div>
        )}

        {/* Questions View */}
        {currentStep === 'questions' && currentQuestion && (
          <div className="space-y-6">
            <QuestionCard
              questionNumber={questionIndex + 1}
              totalQuestions={totalQuestions}
              prompt={currentQuestion.prompt}
              guidance={currentQuestion.guidance}
              questionType={currentQuestion.question_type}
            >
              {currentQuestion.question_type === 'multiple_choice' && (
                <MultipleChoice
                  name={`question-${currentQuestion.id}`}
                  options={currentQuestion.options || []}
                  value={responses[currentQuestion.id]}
                  onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                />
              )}

              {currentQuestion.question_type === 'short_answer' && (
                <ShortAnswer
                  value={responses[currentQuestion.id]}
                  onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                  placeholder={t('practice.typeAnswerHere')}
                />
              )}

              {currentQuestion.question_type === 'essay' && (
                <EssayResponse
                  value={responses[currentQuestion.id]}
                  onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                  placeholder={t('practice.writeEssayHere')}
                  minWords={300}
                  maxWords={500}
                />
              )}
            </QuestionCard>

            <QuestionNavigation
              currentIndex={questionIndex}
              totalQuestions={totalQuestions}
              onPrevious={handlePreviousQuestion}
              onNext={handleNextQuestion}
              canGoNext={canProceedToNext()}
              nextLabel={questionIndex === totalQuestions - 1 ? t('common.submit') : undefined}
            />

            {/* Image upload for handwritten work */}
            <Card className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" padding="sm">
              <ImageUpload
                value={uploadedImages[currentQuestion.id]}
                onUpload={(data) => handleImageUpload(currentQuestion.id, data)}
                onRemove={() => handleRemoveImage(currentQuestion.id)}
              />
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
